import { InvalidUrlError } from '../../errors/errors';
import { BaseGQLService } from '../../services/BaseGQLService';
import { FilterOperator } from '../../types/gql-types';
import { CursorInfo, PaginationResult } from '../../types/pagination';
import { ModelType } from '../../utils/GQLIdEncoder';
import { KeyCertificateMapper } from '../mappers/KeyCertificateMapper';
import { KeyCertificate } from '../models/KeyCertificate';

export enum KeyVariant {
  OrganizationKey = 'OrganizationKey',
  ReportKey = 'Key',
}

export const KEY_CERTS_FRAGMENT = `
pageInfo {
  hasNextPage
  hasPreviousPage
  startCursor
  endCursor
}
edges {
  node {
    id
    subject
    issuer
    notAfter
    signatureAlgorithm {
      digest
    }
  }
}
totalCount
`;

export const GET_KEY_CERTS_QUERY = (variant: KeyVariant) => {
  return `
  query (
    $keyId: ID!,
    $first: Int,
    $last: Int,
    $after: String,
    $before: String
  ) {
    node(id: $keyId) {
      ... on ${variant} {
        certificates(
          first: $first,
          last: $last,
          after: $after,
          before: $before
        ) {
          ${KEY_CERTS_FRAGMENT}
        }
      }
    }
  }`;
};

export class KeyCertificatesService extends BaseGQLService {
  private readonly mapper = new KeyCertificateMapper();

  async getKeyCertificates(
    keyId: number,
    keyVariant: KeyVariant,
    cursor: CursorInfo,
    filters: FilterOperator | null
  ): Promise<PaginationResult<KeyCertificate>> {
    return this.query(
      GET_KEY_CERTS_QUERY(keyVariant),
      {
        keyId: this.gqlIdEncoder.encode(ModelType[keyVariant], keyId),
        ...this.mapCursorInfo(cursor),
        filters,
      },
      (data) => {
        if (!data.node) {
          throw new InvalidUrlError(`Invalid request: could not find ${keyVariant} ${keyId}`);
        }
        const { edges: certificates, pageInfo, totalCount } = data.node.certificates;
        return {
          data: certificates.map((certificate: any) => this.mapper.mapGQLModel(certificate.node)),
          pageInfo,
          totalCount,
        };
      }
    );
  }
}
